'use client';

import { useState, useEffect } from 'react';

import { useSearchParams } from 'next/navigation';

interface UseManualStateProps {
  initialStep?: number;
  steps?: StepData[];
  guide?: GuideData;
  siblings?: GuideData[];
}

export function useManualState({
  initialStep = 0,
  steps = [],
}: UseManualStateProps) {
  const searchParams = useSearchParams();

  const [activeStep, setActiveStep] = useState(initialStep);
  const [activePhoto, setActivePhoto] = useState(0);
  const [sidebar, setSidebar] = useState<'contents' | 'comments' | null>(null);

  useEffect(() => {
    const handleHashChange = () => {
      if (!steps?.length) return;

      const { hash } = window.location;
      if (!hash) {
        setActiveStep(0);
        return;
      }

      const hashValue = hash.slice(1);
      const commentMatch = hashValue.match(/comment-(\d+)/);

      // Use stepId from comment- if possible
      // /fr/guide/2-assemblage-de-lenclosure_688533#753565 -> opens step with 753565 id
      // /fr/guide/2-assemblage-de-lenclosure_688533#comment-753565-71515 -> opens step with 753565 id, opens Comment sidebar
      const stepId = commentMatch ? commentMatch[1] : hashValue;
      const hashNumber = Number(stepId);

      if (!isNaN(hashNumber)) {
        const stepIndex = steps.findIndex(step => step.id === hashNumber);
        if (stepIndex !== -1) {
          setActiveStep(stepIndex);
        }
      }
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [searchParams]);

  // Handle sidebar persistence
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('comment-')) {
      setSidebar('comments');
    } else {
      const savedState = localStorage.getItem('manualSidebar');
      if (savedState === 'contents' || savedState === 'comments') {
        setSidebar(savedState);
      }
    }
  }, []);

  const updateSidebar = (
    newState: 'contents' | 'comments' | null,
    save: boolean = true
  ) => {
    if (save) {
      if (newState === null) {
        localStorage.removeItem('manualSidebar');
      } else {
        localStorage.setItem('manualSidebar', newState);
      }
    }
    setSidebar(newState);
  };

  // Get current step title
  const currentTitle = steps?.[activeStep]?.title
    ? `${activeStep + 1}. ${steps[activeStep].title}`
    : '';

  return {
    activeStep,
    setActiveStep,
    activePhoto,
    setActivePhoto,
    sidebar,
    updateSidebar,
    currentTitle,
  };
}
